import React from "react";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";

type ProjectDetailsProps = {
  projectName: string;
  perms: any[];
  existingProject: boolean;
  subPage: string | undefined;
  onSelectSubPage: Function;
  /**
   * Opens the Wildlife Insights migration (AKL-812). The button renders only when this is
   * supplied, so the caller decides who may see it; the header itself has no opinion.
   */
  onMigrate?: () => void;
};

/**
 * The shared header of the project page: the title (or "New Project"), an optional
 * "Migrate from Wildlife Insights" action, and — for an existing project — the sub-page tabs
 * the user's permissions allow.
 */
const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  projectName,
  perms,
  existingProject,
  subPage,
  onSelectSubPage,
  onMigrate,
}) => {
  const tabStyle = "cursor-pointer py-2 px-4 text-sm font-medium text-center";
  const { t } = useTranslation();
  const canViewDeploymentsAndLocations = perms.some(
    (permission: any) => permission.name === "VIEW_DEPLOYMENTS_LOCATIONS",
  );
  const canViewAiMappings = perms.some(
    (permission: any) => permission.name === "VIEW_PROJECT_DETAILS",
  );

  return (
    <div className="flex flex-row w-full">
      <div className="overflow-x-auto w-full mx-8 mt-8">
        <div className={tailwindClassnames.pageHeaderRow}>
          <h1 className={tailwindClassnames.pageTitle}>
            {existingProject ? projectName : t("New Project")}
          </h1>
          {onMigrate && (
            <button
              type="button"
              className={tailwindClassnames.btnSecondary}
              onClick={onMigrate}
              data-testid="migrate-from-wildlife-insights"
            >
              {t("Migrate from Wildlife Insights")}
            </button>
          )}
        </div>
        {existingProject && (
          <div className="">
            <div className="border border-primary-off-white dark:border-storm-blue-dark border-b-storm-blue-light dark:border-b-sky-blue transition duration-300">
              <div className="flex flex-row">
                <button
                  className={`${tabStyle} ${
                    subPage === "overview"
                      ? `${tailwindClassnames.detailsActiveTab}`
                      : `${tailwindClassnames.detailsInactiveTab}`
                  }`}
                  onClick={() => onSelectSubPage("overview")}
                >
                  {t("Project Details")}
                </button>
                {perms.some(
                  (permission) =>
                    permission.name === "VIEW_USER_DETAILS_IN_PROJECT",
                ) && (
                  <button
                    className={`${tabStyle} ${
                      subPage === "users"
                        ? `${tailwindClassnames.detailsActiveTab}`
                        : `${tailwindClassnames.detailsInactiveTab}`
                    }`}
                    onClick={() => onSelectSubPage("users")}
                  >
                    {t("Users")}
                  </button>
                )}
                {canViewDeploymentsAndLocations && (
                  <>
                    <button
                      className={`${tabStyle} ${
                        subPage === "deployments"
                          ? `${tailwindClassnames.detailsActiveTab}`
                          : `${tailwindClassnames.detailsInactiveTab}`
                      }`}
                      onClick={() => onSelectSubPage("deployments")}
                    >
                      {t("Deployments")}
                    </button>
                    <button
                      className={`${tabStyle} ${
                        subPage === "locations"
                          ? `${tailwindClassnames.detailsActiveTab}`
                          : `${tailwindClassnames.detailsInactiveTab}`
                      }`}
                      onClick={() => onSelectSubPage("locations")}
                    >
                      {t("Locations")}
                    </button>
                  </>
                )}
                {canViewAiMappings && (
                  <button
                    className={`${tabStyle} ${
                      subPage === "ai-mappings"
                        ? `${tailwindClassnames.detailsActiveTab}`
                        : `${tailwindClassnames.detailsInactiveTab}`
                    }`}
                    onClick={() => onSelectSubPage("ai-mappings")}
                  >
                    {t("AI Mappings")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
